import type { Month13, Day13 } from '@/lib/calendar13Types';
import { cn } from '@/lib/utils';

export interface YearRingProps {
  months: Month13[];
  selectedMonth: number;
  today: Day13;
  onSelectMonth: (month: number) => void;
  className?: string;
}

export function YearRing({ months, selectedMonth, today, onSelectMonth, className }: YearRingProps) {
  const size = 320;
  const center = size / 2;
  const outerRadius = 145;
  const innerRadius = 80;
  const segmentGap = 1; // degrees
  const segmentAngle = (360 - 13 * segmentGap) / 13;
  
  // Create arc path for a ring segment (handles both clockwise and counter-clockwise)
  const createArcPath = (
    startAngle: number,
    endAngle: number,
    innerR: number,
    outerR: number
  ) => {
    const startRad = (startAngle - 90) * (Math.PI / 180);
    const endRad = (endAngle - 90) * (Math.PI / 180);
    
    const x1 = center + outerR * Math.cos(startRad);
    const y1 = center + outerR * Math.sin(startRad);
    const x2 = center + outerR * Math.cos(endRad);
    const y2 = center + outerR * Math.sin(endRad);
    const x3 = center + innerR * Math.cos(endRad);
    const y3 = center + innerR * Math.sin(endRad);
    const x4 = center + innerR * Math.cos(startRad);
    const y4 = center + innerR * Math.sin(startRad);
    
    // For counter-clockwise (negative angle diff), use sweep-flag 0
    const angleDiff = endAngle - startAngle;
    const largeArc = Math.abs(angleDiff) > 180 ? 1 : 0;
    const sweepOuter = angleDiff > 0 ? 1 : 0;
    const sweepInner = angleDiff > 0 ? 0 : 1;
    
    return `
      M ${x1} ${y1}
      A ${outerR} ${outerR} 0 ${largeArc} ${sweepOuter} ${x2} ${y2}
      L ${x3} ${y3}
      A ${innerR} ${innerR} 0 ${largeArc} ${sweepInner} ${x4} ${y4}
      Z
    `;
  };
  
  // Calculate label position (middle of segment) - clockwise from top
  const getLabelPosition = (monthIndex: number) => {
    // Segments go clockwise from top: 1 at top, 2 to the right
    const startAngle = monthIndex * (segmentAngle + segmentGap);
    const midAngle = startAngle + segmentAngle / 2;
    const midRad = (midAngle - 90) * (Math.PI / 180);
    const labelRadius = (innerRadius + outerRadius) / 2;
    
    return {
      x: center + labelRadius * Math.cos(midRad),
      y: center + labelRadius * Math.sin(midRad),
    };
  };

  return (
    <div className={cn("relative w-full max-w-[220px] aspect-square mx-auto", className)}>
      <svg viewBox={`0 0 ${size} ${size}`} className="w-full h-full">
        {/* Outer glow ring */}
        <circle
          cx={center}
          cy={center}
          r={outerRadius + 5}
          fill="none"
          stroke="hsl(var(--primary) / 0.1)"
          strokeWidth="10"
          className="animate-pulse-glow"
        />
        
        {/* Month ring segments */}
        {months.map((month, idx) => {
          // Clockwise layout: 1 at top, 2 to the right
          const startAngle = idx * (segmentAngle + segmentGap);
          const endAngle = startAngle + segmentAngle;
          const isSelected = month.number === selectedMonth;
          const isCurrentMonth = today.month === month.number;
          const labelPos = getLabelPosition(idx);
          
          return (
            <g key={month.number} className="cursor-pointer">
              {/* Segment path */}
              <path
                d={createArcPath(startAngle, endAngle, innerRadius, outerRadius)}
                fill={isSelected 
                  ? `hsl(var(--month-${month.number}))` 
                  : `hsl(var(--month-${month.number}) / 0.2)`
                }
                stroke={`hsl(var(--month-${month.number}))`}
                strokeWidth={isCurrentMonth ? 2 : 1}
                className={cn(
                  "transition-all duration-300 hover:opacity-90",
                  isSelected && "drop-shadow-[0_0_8px_hsl(var(--month-" + month.number + ")/0.5)]"
                )}
                onClick={() => onSelectMonth(month.number)}
              />
              
              {/* Month number label */}
              <text
                x={labelPos.x}
                y={labelPos.y}
                textAnchor="middle"
                dominantBaseline="central"
                fill={isSelected ? 'hsl(var(--primary-foreground))' : 'hsl(var(--foreground))'}
                fontSize="11"
                fontWeight="600"
                className="pointer-events-none font-display"
              >
                {month.number}
              </text>
            </g>
          );
        })}
        
        {/* Inner circle background */}
        <circle
          cx={center}
          cy={center}
          r={innerRadius - 5}
          fill="hsl(var(--background))"
          stroke="hsl(var(--border))"
          strokeWidth="1"
        />
        
        {/* Center content */}
        <text
          x={center}
          y={center - 15}
          textAnchor="middle"
          dominantBaseline="central"
          fontSize="28"
          className="pointer-events-none"
        >
          {months[selectedMonth - 1]?.glyph || "✨"}
        </text>
        <text
          x={center}
          y={center + 12}
          textAnchor="middle"
          dominantBaseline="central"
          fill="hsl(var(--foreground))"
          fontSize="10"
          fontWeight="500"
          className="pointer-events-none font-display"
        >
          {months[selectedMonth - 1]?.name || ""}
        </text>
        <text
          x={center}
          y={center + 28}
          textAnchor="middle"
          dominantBaseline="central"
          fill="hsl(var(--muted-foreground))"
          fontSize="9"
          className="pointer-events-none"
        >
          Moon {selectedMonth}
        </text>
      </svg>
    </div>
  );
}
